import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, Trash2, Download, FileText } from "lucide-react";
import { downloadDocument, prettySize } from "@/lib/download";

const STAGES = ["", "methodology", "market", "product", "admin", "metrics"];
const input = "w-full bg-transparent border hairline px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--brand-primary)]";

export default function DocumentsTab() {
  const [docs, setDocs] = useState([]);
  const [modules, setModules] = useState([]);
  const [title, setTitle] = useState("");
  const [stage, setStage] = useState("");
  const [moduleId, setModuleId] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/documents").then(r => setDocs(r.data)).catch(() => {});
    api.get("/modules").then(r => setModules(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Choose a file first"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title || file.name);
    fd.append("stage", stage);
    fd.append("module_id", moduleId);
    try {
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setTitle(""); setStage(""); setModuleId("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title}"?`)) return;
    await api.delete(`/documents/${d.id}`);
    toast.success("Deleted");
    load();
  };

  const moduleTitle = (id) => modules.find(m => m.id === id)?.title;

  return (
    <div className="space-y-6" data-testid="documents-tab">
      <div className="border hairline bg-white p-5 space-y-3">
        <div className="label-eyebrow text-[var(--brand-primary)]">Upload a document (PDF, Word, Excel, PPT, images…)</div>
        <div className="grid md:grid-cols-2 gap-3">
          <input type="file" ref={fileRef} className={input} data-testid="doc-file" />
          <input className={input} placeholder="Title (optional — defaults to filename)" value={title} onChange={e => setTitle(e.target.value)} data-testid="doc-title" />
          <select className={input} value={stage} onChange={e => setStage(e.target.value)} data-testid="doc-stage">
            {STAGES.map(s => <option key={s} value={s}>{s ? s : "No stage"}</option>)}
          </select>
          <select className={input} value={moduleId} onChange={e => setModuleId(e.target.value)} data-testid="doc-module">
            <option value="">Not attached to a module</option>
            {modules.map(m => <option key={m.id} value={m.id}>{m.stage} · {m.title}</option>)}
          </select>
        </div>
        <button disabled={busy} className="btn-primary flex items-center gap-2" onClick={upload} data-testid="doc-upload-btn">
          <UploadCloud size={14} /> {busy ? "Uploading…" : "Upload document"}
        </button>
      </div>

      <div className="space-y-2">
        {docs.length === 0 && <div className="text-sm text-neutral-500">No documents yet in this workspace.</div>}
        {docs.map(d => (
          <div key={d.id} className="border hairline bg-white p-4 flex items-center gap-4" data-testid={`doc-row-${d.id}`}>
            <FileText size={18} className="text-[var(--brand-primary)] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{d.title}</div>
              <div className="text-xs text-neutral-500 font-mono truncate">
                {d.original_filename} · {prettySize(d.size)}
                {d.stage ? ` · ${d.stage}` : ""}{d.module_id ? ` · ${moduleTitle(d.module_id) || "module"}` : ""}
              </div>
            </div>
            <button className="btn-ghost text-xs flex items-center gap-1" onClick={() => downloadDocument(d)} data-testid={`doc-download-${d.id}`}><Download size={12} /> Download</button>
            <button className="btn-ghost text-xs flex items-center gap-1" onClick={() => remove(d)} data-testid={`doc-delete-${d.id}`}><Trash2 size={12} /> Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}
